<template>
  <div class="ui-order-item rounded-xl px-3 py-2 relative" :class="[$theme.cardBg]">
    <div
        v-if="order.user_task != null"
        class="absolute inset-0 bg-gray-800 bg-opacity-70 rounded-xl flex items-center justify-center z-10"
    >
      <svg
          class="animate-spin h-8 w-8 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
      >
        <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
        ></circle>
        <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
    </div>
    <div class="flex">
      <div class="w-full flex items-center justify-between font-semibold whitespace-nowrap">
        <div class="flex items-center mb-2">
          <div>
            <div class="relative flex items-center">
              <CryptoIcon :symbol="order.symbol" />
              <template v-if="order.symbol.includes('USDT')">
                <img src="../../../node_modules/cryptocurrency-icons/svg/color/usdt.svg" class="w-5 relative right-2">
              </template>
              <template v-if="order.symbol.includes('USDC')">
                <img src="../../../node_modules/cryptocurrency-icons/svg/color/usdc.svg" class="w-5 relative right-2">
              </template>
              <span>{{ order.symbol }} x{{ parseInt(order.leverage) }}</span>
            </div>
          </div>
          <div class="pl-3">
            <template v-if="order.side === 'long'">
              <div class="text-white text-sm font-normal px-2 rounded-full" :class="[$theme.bgSuccessPnlColor]">LONG</div>
            </template>
            <template v-else>
              <div class="text-white text-sm font-normal px-2 rounded-full" :class="[$theme.bgDangerColor]">SHORT</div>
            </template>
          </div>
        </div>
        <div v-if="is_edit === true" class="flex items-center font-normal text-sm">
          <Menu as="div" class="relative inline-block text-left">
            <div>
              <MenuButton class="flex items-center rounded-full focus:outline-none" :class="[$theme.menuBtnBg, $theme.menuBtnTextColor]">
                <span class="sr-only">Open options</span>
                <EllipsisVerticalIcon class="h-5 w-5" aria-hidden="true" />
              </MenuButton>
            </div>

            <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
              <MenuItems class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div class="py-1">
                  <MenuItem v-slot="{ active }">
                    <a @click="closePosition(order)" href="javascript:void(0)" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']">
                      {{ $t('close_position') }}
                    </a>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>
    <div class="text-sm pb-2">
      <div class="whitespace-nowrap flex items-center justify-between py-1">
        <div class="" :class="[$theme.cardLabelTextColor]">
          <template v-if="order.closed">
            {{ $t('in_position') }}
          </template>
          <template v-else>
            {{ $t('margin') }}
          </template>
        </div>
        <template v-if="order.closed">
          <div class="">
            {{ roundAmount(order.initial_margin, 2) }} USDT
          </div>
        </template>
        <template v-else>
          <TheTooltip :id="`o_${order.id}`" :message="`${$t('in_position')} ${order.notional} USDT`">
            <div class="text-sm">{{ roundAmount(order.initial_margin, 2) }} USDT</div>
          </TheTooltip>
        </template>
      </div>
      <div class="whitespace-nowrap flex items-center justify-between py-1">
        <div class="" :class="[$theme.cardLabelTextColor]">{{ $t('size') }}</div>
        <div class="" :class="[
          order.side === 'long' ? $theme.successPnlColor : $theme.dangerPnlColor
        ]">
          {{ order.closed ? parseFloat(order.orig_qty) : parseFloat(order.current_qty) }} {{ order.symbol.replace('USDT', '') }}
        </div>
      </div>
      <div class="whitespace-nowrap flex justify-between py-1">
        <div class="" :class="[$theme.cardLabelTextColor]">P&L</div>
        <ShowPnl
          :pnl="order.closed ? order.unrealized_pnl : order.unrealized_pnl"
          :percent="order.percentage || 0"
          :is_close="order.closed"
        />
      </div>
      <template v-if="displayButton && !order.closed">
        <div class="flex justify-end mt-2 mb-3">
          <button
              @click="closePosition(order)"
              class="px-3 py-2 text-xs font-semibold rounded-lg text-white transition-all animate-pulse"
              :class="[$theme.bgSuccessPnlColor]"
          >
            {{ $t('close_with_profit') }} {{ order.percentage }}%
          </button>
        </div>
      </template>
    </div>
    <Disclosure as="div" class="w-full border-t py-1 text-sm" :class="[$theme.cardBorderColor]" v-slot="{ open }">
      <dt class="flex justify-between items-center w-full">
        <div class="flex items-center">
          <img :src="`/img/${order.exchange.exchange_slug}.png`" class="w-4 rounded-full mr-1">
          {{ order.exchange.title }} <span class="opacity-50 pl-2">ID: {{ order.exchange.id }}</span>
        </div>
        <DisclosureButton class="flex items-start text-left" :class="[$theme.cardLabelTextColor]">
          <span class="ml-1 flex h-5 items-center">
            <PlusSmallIcon v-if="!open" class="h-5 w-5" aria-hidden="true" />
            <MinusSmallIcon v-else class="h-5 w-5" aria-hidden="true" />
          </span>
        </DisclosureButton>
      </dt>
      <DisclosurePanel as="dd" class="w-full pt-4">
        <div class="">
          <div class="flex items-center justify-between py-1">
            <div class="" :class="[$theme.cardLabelTextColor]">{{ $t('opening_price') }}</div>
            <div class="text-sm">
              {{ order.entry_price || '0' }}
            </div>
          </div>
          <div class="flex items-center justify-between py-1">
            <div class="" :class="[$theme.cardLabelTextColor]">{{ $t('price_liquidation') }}</div>
            <div class="text-sm" :class="[$theme.dangerPnlColor]">
              {{ order.liquidation_price || '0' }}
            </div>
          </div>
          <div class="flex items-center justify-between py-1">
            <div class="" :class="[$theme.cardLabelTextColor]">TP/SL</div>
            <div class="text-sm">
              <template v-if="order.takeprofit_price">
                <span :class="[$theme.successPnlColor]">{{ order.takeprofit_price }}</span>
              </template>
              <template v-else>
                <span>-</span>
              </template>
              /
              <template v-if="order.stoploss_price">
                <span v-if="order.position === 'long'" :class="[$theme.dangerPnlColor]">
                  {{ parseFloat(order.stoploss_price) > 0.00001 ? order.stoploss_price : '≤0.00001' }}
                </span>
                <span v-if="order.position === 'short'" :class="[$theme.successPnlColor]">
                  {{ parseFloat(order.stoploss_price) < 999999 ? order.stoploss_price : '≥999999' }}
                </span>
              </template>
              <template v-else>
                <span>-</span>
              </template>
            </div>
          </div>
          <div class="flex items-center justify-between py-1">
            <div class="" :class="[$theme.cardLabelTextColor]">{{ $t('created_at') }}</div>
            <div class="text-sm">{{ order.created_at_f }}</div>
          </div>
          <div v-if="order.closed" class="flex items-center justify-between py-1">
            <div class="" :class="[$theme.cardLabelTextColor]">{{ $t('closing_at') }}</div>
            <div class="text-sm">{{ order.closed_at_f || '-' }}</div>
          </div>
        </div>
      </DisclosurePanel>
    </Disclosure>
  </div>
</template>
<script>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import {EllipsisVerticalIcon} from "@heroicons/vue/20/solid";
import CryptoIcon from "@/components/icons/CryptoIcon.vue";
import TheTooltip from "@/components/elem/TheTooltip.vue";
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/vue/24/outline'
import ShowPnl from "@/components/elem/ShowPnl.vue";
import {capitalizeFirstLetter} from "@/helpers/stringHelper"
import {roundAmount} from "@/helpers/numberHelper"

export default {
  props: [
    'order',
    'is_edit'
  ],

  components: {
    EllipsisVerticalIcon,
    CryptoIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TheTooltip,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    MinusSmallIcon,
    PlusSmallIcon,
    ShowPnl
  },

  watch: {
    'order.pnl_percentage': {
      handler(newVal) {
        const pnl = parseFloat(newVal)

        clearTimeout(this.pnlTimeout)

        if (pnl > this.close_threshold && !this.displayButton) {
          this.pnlTimeout = setTimeout(() => {
            this.displayButton = true
          }, 2000)
        }
        else if (pnl < (this.close_threshold - 2) && this.displayButton) {
          this.pnlTimeout = setTimeout(() => {
            this.displayButton = false
          }, 2000)
        }
      },
      immediate: true
    }
  },

  methods: {
    closePosition(order) {
      if (order.user_task !== null) return;

      console.log('Load orders...');

      this.$api.post(`/positions/${order.id}/close`).then((result) => {
        console.log(result.data)
        this.$emit('close-position', 1);
      }).catch(e => {
        alert(e.message)
        this.error = e
      })
    }
  },

  data() {
    return {
      capitalizeFirstLetter,
      roundAmount,
      displayButton: false,
      pnlTimeout: null,
      close_threshold: 10000,
    }
  },

  mounted() {
    this.close_threshold = this.$settings.close_threshold;
  },

  beforeUnmount() {
    clearTimeout(this.pnlTimeout)
  }
};
</script>

<style>
@keyframes pulse-opacity {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}
.animate-pulse-opacity {
  animation: pulse-opacity 2s infinite;
}
</style>
